import * as v from "valibot";

import {
  ASSET_KINDS,
  LOCAL_PORTFOLIO_STORAGE_SCHEMA_VERSION,
  MAX_ASSET_REFERENCE_LENGTH,
  MAX_IDENTIFIER_LENGTH,
  MAX_INSTRUMENT_NAME_LENGTH,
  MAX_MANUAL_QUOTES,
  MAX_MEMO_LENGTH,
  MAX_NICKNAME_LENGTH,
  MAX_PURCHASE_LOTS_PER_HOLDING,
  MAX_STORED_PORTFOLIO_HOLDINGS,
  MAX_TICKER_LENGTH,
  MAX_VENUE_LENGTH,
  PORTFOLIO_SCHEMA_VERSION,
  QUOTE_STATUSES,
  QUOTE_SOURCES,
  SLIME_PALETTE_IDS,
  SLIME_PATTERN_IDS,
} from "./constants";
import {
  isIso4217CurrencyCode,
  type Iso4217CurrencyCode,
} from "./currency";
import {
  isDecimalString,
  isNonNegativeDecimalString,
  isPositiveDecimalString,
  type DecimalString,
  type NonNegativeDecimalString,
  type PositiveDecimalString,
} from "./decimal";

const STRICT_OBJECT_MESSAGE = "지원하지 않는 필드가 포함되어 있습니다.";
const TRIMMED_TEXT_MESSAGE = "앞뒤 공백 없이 입력해 주세요.";
const IDENTIFIER_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]*$/;
const TICKER_PATTERN = /^[A-Z0-9][A-Z0-9.-]*$/;
const VENUE_PATTERN = /^[A-Z0-9][A-Z0-9._:-]*$/;
const ASSET_REFERENCE_PATTERN = /^[a-z0-9][a-z0-9._:-]*$/;
const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;
const MARKET_TIME_ZONE_PATTERN =
  /^(?:UTC|[A-Za-z_+-]+(?:\/[A-Za-z0-9_+-]+)+)$/;

function isTrimmed(value: string): boolean {
  return value === value.trim();
}

function isCalendarDate(
  year: number,
  month: number,
  day: number,
): boolean {
  if (year < 1 || year > 9_999 || month < 1 || month > 12 || day < 1) {
    return false;
  }

  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [
    31,
    leapYear ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ][month - 1];

  return day <= (daysInMonth ?? 0);
}

const MILLISECONDS_PER_DAY = 86_400_000;

/** Proleptic Gregorian calendar → days since 1970-01-01 (Howard Hinnant). */
function daysFromCivil(year: number, month: number, day: number): number {
  const adjustedYear = year - (month <= 2 ? 1 : 0);
  const era = Math.floor(adjustedYear / 400);
  const yearOfEra = adjustedYear - era * 400;
  const adjustedMonth = month + (month > 2 ? -3 : 9);
  const dayOfYear =
    Math.floor((153 * adjustedMonth + 2) / 5) + day - 1;
  const dayOfEra =
    yearOfEra * 365 +
    Math.floor(yearOfEra / 4) -
    Math.floor(yearOfEra / 100) +
    dayOfYear;
  return era * 146_097 + dayOfEra - 719_468;
}

const MIN_V1_TIMESTAMP_MILLISECONDS =
  daysFromCivil(1, 1, 1) * MILLISECONDS_PER_DAY;
const MAX_V1_TIMESTAMP_MILLISECONDS =
  daysFromCivil(9_999, 12, 31) * MILLISECONDS_PER_DAY +
  MILLISECONDS_PER_DAY -
  1;

export function isIsoCalendarDate(value: unknown): value is string {
  if (typeof value !== "string") {
    return false;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return false;
  }

  return isCalendarDate(
    Number(match[1]),
    Number(match[2]),
    Number(match[3]),
  );
}

/**
 * RFC 3339 timestamp with an explicit Z/offset. Factories emit canonical UTC
 * (`Date#toISOString`), while validators also accept interoperable offsets.
 */
export function isRfc3339Timestamp(value: unknown): value is string {
  if (typeof value !== "string") {
    return false;
  }

  return rfc3339TimestampToEpochMilliseconds(value) !== null;
}

/**
 * The v1 contract intentionally stores at most millisecond precision. This
 * keeps chronology exact across JS runtimes and matches Date#toISOString.
 */
export function rfc3339TimestampToEpochMilliseconds(
  value: unknown,
): number | null {
  if (typeof value !== "string") {
    return null;
  }

  const match =
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?(Z|([+-])(\d{2}):(\d{2}))$/.exec(
      value,
    );
  if (!match) {
    return null;
  }

  const dateIsValid = isCalendarDate(
    Number(match[1]),
    Number(match[2]),
    Number(match[3]),
  );
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const offsetHour = match[10] === undefined ? 0 : Number(match[10]);
  const offsetMinute = match[11] === undefined ? 0 : Number(match[11]);
  const offsetIsValid =
    offsetHour < 14 || (offsetHour === 14 && offsetMinute === 0);

  if (
    !dateIsValid ||
    hour > 23 ||
    minute > 59 ||
    second > 59 ||
    offsetHour > 14 ||
    offsetMinute > 59 ||
    !offsetIsValid
  ) {
    return null;
  }

  const fraction = (match[7] ?? "").padEnd(3, "0");
  const localMilliseconds =
    daysFromCivil(
      Number(match[1]),
      Number(match[2]),
      Number(match[3]),
    ) *
      MILLISECONDS_PER_DAY +
    hour * 3_600_000 +
    minute * 60_000 +
    second * 1_000 +
    Number(fraction);
  const offsetDirection = match[9] === "+" ? 1 : match[9] === "-" ? -1 : 0;
  const offsetMilliseconds =
    offsetDirection * (offsetHour * 60 + offsetMinute) * 60_000;
  const utcMilliseconds = localMilliseconds - offsetMilliseconds;

  // An offset at a year boundary can move the UTC instant outside the v1
  // four-digit year range even though the local date itself is valid.
  return utcMilliseconds >= MIN_V1_TIMESTAMP_MILLISECONDS &&
    utcMilliseconds <= MAX_V1_TIMESTAMP_MILLISECONDS
    ? utcMilliseconds
    : null;
}

export function toCanonicalUtcTimestamp(value: Date | string): string | null {
  if (typeof value === "string") {
    const milliseconds = rfc3339TimestampToEpochMilliseconds(value);
    return milliseconds === null
      ? null
      : new Date(milliseconds).toISOString();
  }

  if (!Number.isFinite(value.getTime())) {
    return null;
  }
  const canonical = value.toISOString();
  return isRfc3339Timestamp(canonical) ? canonical : null;
}

const IdentifierSchema = v.pipe(
  v.string("식별자는 문자열이어야 합니다."),
  v.minLength(1, "식별자를 입력해 주세요."),
  v.maxLength(
    MAX_IDENTIFIER_LENGTH,
    `식별자는 ${MAX_IDENTIFIER_LENGTH}자 이하여야 합니다.`,
  ),
  v.regex(IDENTIFIER_PATTERN, "식별자 형식이 올바르지 않습니다."),
);

const TickerSchema = v.pipe(
  v.string("티커는 문자열이어야 합니다."),
  v.minLength(1, "티커를 입력해 주세요."),
  v.maxLength(
    MAX_TICKER_LENGTH,
    `티커는 ${MAX_TICKER_LENGTH}자 이하여야 합니다.`,
  ),
  v.regex(
    TICKER_PATTERN,
    "티커는 대문자 영문, 숫자, 점, 하이픈만 사용할 수 있습니다.",
  ),
);

const InstrumentNameSchema = v.pipe(
  v.string("종목명은 문자열이어야 합니다."),
  v.minLength(1, "종목명을 입력해 주세요."),
  v.maxLength(
    MAX_INSTRUMENT_NAME_LENGTH,
    `종목명은 ${MAX_INSTRUMENT_NAME_LENGTH}자 이하여야 합니다.`,
  ),
  v.check(isTrimmed, TRIMMED_TEXT_MESSAGE),
);

const VenueSchema = v.pipe(
  v.string("시장·거래소는 문자열이어야 합니다."),
  v.minLength(1, "시장·거래소를 입력해 주세요."),
  v.maxLength(
    MAX_VENUE_LENGTH,
    `시장·거래소는 ${MAX_VENUE_LENGTH}자 이하여야 합니다.`,
  ),
  v.regex(
    VENUE_PATTERN,
    "시장·거래소는 대문자 영문, 숫자, 점, 밑줄, 콜론, 하이픈만 사용할 수 있습니다.",
  ),
);

const CurrencySchema = v.custom<Iso4217CurrencyCode>(
  isIso4217CurrencyCode,
  "지원하는 ISO 4217 통화 코드를 입력해 주세요.",
);

const DecimalSchema = v.custom<DecimalString>(
  isDecimalString,
  "소수는 부호·숫자·소수점만 사용하고 총 30자리, 소수 12자리 이하여야 합니다.",
);

const NonNegativeDecimalSchema = v.custom<NonNegativeDecimalString>(
  isNonNegativeDecimalString,
  "0 이상의 소수를 총 30자리, 소수 12자리 이내로 입력해 주세요.",
);

const PositiveDecimalSchema = v.custom<PositiveDecimalString>(
  isPositiveDecimalString,
  "0보다 큰 소수를 총 30자리, 소수 12자리 이내로 입력해 주세요.",
);

const IsoDateSchema = v.custom<string>(
  isIsoCalendarDate,
  "실제로 존재하는 YYYY-MM-DD 날짜를 입력해 주세요.",
);

const TimestampSchema = v.custom<string>(
  isRfc3339Timestamp,
  "시간대가 포함된 RFC 3339 날짜·시간을 입력해 주세요.",
);

const AssetReferenceSchema = v.pipe(
  v.string("에셋 식별자는 문자열이어야 합니다."),
  v.minLength(1, "에셋 식별자를 입력해 주세요."),
  v.maxLength(
    MAX_ASSET_REFERENCE_LENGTH,
    `에셋 식별자는 ${MAX_ASSET_REFERENCE_LENGTH}자 이하여야 합니다.`,
  ),
  v.regex(
    ASSET_REFERENCE_PATTERN,
    "에셋 식별자 형식이 올바르지 않습니다.",
  ),
);

export const HoldingCustomizationV1Schema = v.strictObject(
  {
    /**
     * System-assigned registration color. It survives "reset customization"
     * while user-authored palette/body overrides are removed.
     */
    basePaletteId: v.optional(v.picklist(SLIME_PALETTE_IDS)),
    /**
     * System-assigned first accessory. User-authored `hatId` wins while
     * customization reset reveals this durable default again.
     */
    baseHatId: v.optional(AssetReferenceSchema),
    paletteId: v.optional(v.picklist(SLIME_PALETTE_IDS)),
    bodyColor: v.optional(
      v.pipe(
        v.string("몸 색상은 문자열이어야 합니다."),
        v.regex(HEX_COLOR_PATTERN, "몸 색상은 #RRGGBB 형식이어야 합니다."),
      ),
    ),
    accentColor: v.optional(
      v.pipe(
        v.string("강조 색상은 문자열이어야 합니다."),
        v.regex(HEX_COLOR_PATTERN, "강조 색상은 #RRGGBB 형식이어야 합니다."),
      ),
    ),
    patternId: v.optional(v.picklist(SLIME_PATTERN_IDS)),
    hatId: v.optional(AssetReferenceSchema),
    nickname: v.optional(
      v.pipe(
        v.string("별명은 문자열이어야 합니다."),
        v.maxLength(
          MAX_NICKNAME_LENGTH,
          `별명은 ${MAX_NICKNAME_LENGTH}자 이하여야 합니다.`,
        ),
        v.check(isTrimmed, TRIMMED_TEXT_MESSAGE),
      ),
    ),
  },
  STRICT_OBJECT_MESSAGE,
);

export const PurchaseLotV1Schema = v.strictObject(
  {
    lotId: IdentifierSchema,
    quantity: PositiveDecimalSchema,
    unitPrice: NonNegativeDecimalSchema,
    purchasedAt: v.optional(IsoDateSchema),
  },
  STRICT_OBJECT_MESSAGE,
);

export const AverageCostBasisV1Schema = v.strictObject(
  {
    mode: v.literal("average", "원가 입력 방식이 올바르지 않습니다."),
    currency: v.optional(CurrencySchema),
    averageBuyPrice: NonNegativeDecimalSchema,
  },
  STRICT_OBJECT_MESSAGE,
);

export const LotsCostBasisV1Schema = v.strictObject(
  {
    mode: v.literal("lots", "원가 입력 방식이 올바르지 않습니다."),
    currency: v.optional(CurrencySchema),
    purchaseLots: v.pipe(
      v.array(PurchaseLotV1Schema, "매수 내역은 배열이어야 합니다."),
      v.minLength(1, "매수 내역을 한 개 이상 입력해 주세요."),
      v.maxLength(
        MAX_PURCHASE_LOTS_PER_HOLDING,
        `종목당 매수 내역은 ${MAX_PURCHASE_LOTS_PER_HOLDING}개 이하여야 합니다.`,
      ),
    ),
  },
  STRICT_OBJECT_MESSAGE,
);

export const CostBasisV1Schema = v.variant(
  "mode",
  [AverageCostBasisV1Schema, LotsCostBasisV1Schema],
  "원가 입력 방식은 average 또는 lots여야 합니다.",
);

export const HoldingV1Schema = v.strictObject(
  {
    holdingId: IdentifierSchema,
    assetKind: v.picklist(ASSET_KINDS, "지원하는 자산 종류를 선택해 주세요."),
    ticker: v.optional(TickerSchema),
    name: v.optional(InstrumentNameSchema),
    market: v.optional(VenueSchema),
    exchange: v.optional(VenueSchema),
    currency: v.optional(CurrencySchema),
    quantity: PositiveDecimalSchema,
    costBasis: v.optional(CostBasisV1Schema),
    firstPurchasedAt: v.optional(IsoDateSchema),
    memo: v.optional(
      v.pipe(
        v.string("메모는 문자열이어야 합니다."),
        v.maxLength(
          MAX_MEMO_LENGTH,
          `메모는 ${MAX_MEMO_LENGTH}자 이하여야 합니다.`,
        ),
      ),
    ),
    customization: v.optional(HoldingCustomizationV1Schema),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
  },
  STRICT_OBJECT_MESSAGE,
);

export const PortfolioDocumentV1Schema = v.strictObject(
  {
    schemaVersion: v.literal(
      PORTFOLIO_SCHEMA_VERSION,
      "지원하지 않는 포트폴리오 스키마 버전입니다.",
    ),
    portfolioId: IdentifierSchema,
    currency: CurrencySchema,
    farmUnit: v.optional(PositiveDecimalSchema),
    holdings: v.pipe(
      v.array(HoldingV1Schema, "보유 종목은 배열이어야 합니다."),
      v.minLength(1, "보유 종목을 한 개 이상 입력해 주세요."),
      v.maxLength(
        MAX_STORED_PORTFOLIO_HOLDINGS,
        `저장된 보유 종목은 ${MAX_STORED_PORTFOLIO_HOLDINGS}개 이하여야 합니다.`,
      ),
    ),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
  },
  STRICT_OBJECT_MESSAGE,
);

export const ManualQuoteV1Schema = v.strictObject(
  {
    holdingId: IdentifierSchema,
    quoteStatus: v.picklist(
      QUOTE_STATUSES,
      "지원하는 시세 상태를 선택해 주세요.",
    ),
    currentPrice: v.optional(NonNegativeDecimalSchema),
    marketValue: v.optional(NonNegativeDecimalSchema),
    dailyReturnPct: v.optional(DecimalSchema),
    dailyChangeAmount: v.optional(DecimalSchema),
    source: v.optional(v.picklist(QUOTE_SOURCES)),
    observedAt: v.optional(TimestampSchema),
    marketDate: v.optional(IsoDateSchema),
    marketTimeZone: v.optional(
      v.pipe(
        v.string("시장 시간대는 문자열이어야 합니다."),
        v.maxLength(64, "시장 시간대는 64자 이하여야 합니다."),
        v.regex(
          MARKET_TIME_ZONE_PATTERN,
          "시장 시간대는 IANA time zone 형식이어야 합니다.",
        ),
      ),
    ),
    providerSymbol: v.optional(TickerSchema),
    sourceCurrency: v.optional(CurrencySchema),
    valuationCurrency: v.optional(CurrencySchema),
  },
  STRICT_OBJECT_MESSAGE,
);

export const ManualQuoteSetV1Schema = v.strictObject(
  {
    schemaVersion: v.literal(
      PORTFOLIO_SCHEMA_VERSION,
      "지원하지 않는 수동 시세 스키마 버전입니다.",
    ),
    portfolioId: IdentifierSchema,
    currency: CurrencySchema,
    asOf: TimestampSchema,
    marketDate: IsoDateSchema,
    quotes: v.pipe(
      v.array(ManualQuoteV1Schema, "수동 시세는 배열이어야 합니다."),
      v.maxLength(
        MAX_MANUAL_QUOTES,
        `수동 시세는 ${MAX_MANUAL_QUOTES}개 이하여야 합니다.`,
      ),
    ),
  },
  STRICT_OBJECT_MESSAGE,
);

export const LocalPortfolioRecordV1Schema = v.strictObject(
  {
    storageSchemaVersion: v.literal(
      LOCAL_PORTFOLIO_STORAGE_SCHEMA_VERSION,
      "지원하지 않는 로컬 저장 스키마 버전입니다.",
    ),
    document: PortfolioDocumentV1Schema,
    manualQuotes: v.optional(ManualQuoteSetV1Schema),
  },
  STRICT_OBJECT_MESSAGE,
);

type MutablePurchaseLotV1 = v.InferOutput<typeof PurchaseLotV1Schema>;
type MutableHoldingCustomizationV1 = v.InferOutput<
  typeof HoldingCustomizationV1Schema
>;
type MutableAverageCostBasisV1 = v.InferOutput<
  typeof AverageCostBasisV1Schema
>;
type MutableLotsCostBasisV1 = v.InferOutput<typeof LotsCostBasisV1Schema>;
type MutableHoldingV1 = v.InferOutput<typeof HoldingV1Schema>;
type MutablePortfolioDocumentV1 = v.InferOutput<
  typeof PortfolioDocumentV1Schema
>;
type MutableManualQuoteV1 = v.InferOutput<typeof ManualQuoteV1Schema>;
type MutableManualQuoteSetV1 = v.InferOutput<typeof ManualQuoteSetV1Schema>;
type MutableLocalPortfolioRecordV1 = v.InferOutput<
  typeof LocalPortfolioRecordV1Schema
>;

export type AssetKindV1 = (typeof ASSET_KINDS)[number];
export type QuoteStatusV1 = (typeof QUOTE_STATUSES)[number];
export type QuoteSourceV1 = (typeof QUOTE_SOURCES)[number];
export type PurchaseLotV1 = Readonly<MutablePurchaseLotV1>;
export type HoldingCustomizationV1 = Readonly<MutableHoldingCustomizationV1>;
export type AverageCostBasisV1 = Readonly<MutableAverageCostBasisV1>;
export type LotsCostBasisV1 = Readonly<
  Omit<MutableLotsCostBasisV1, "purchaseLots"> & {
    readonly purchaseLots: readonly PurchaseLotV1[];
  }
>;
export type CostBasisV1 = AverageCostBasisV1 | LotsCostBasisV1;
export type HoldingV1 = Readonly<
  Omit<MutableHoldingV1, "costBasis" | "customization"> & {
    readonly costBasis?: CostBasisV1;
    readonly customization?: HoldingCustomizationV1;
  }
>;
export type PortfolioDocumentV1 = Readonly<
  Omit<MutablePortfolioDocumentV1, "holdings"> & {
    readonly holdings: readonly HoldingV1[];
  }
>;
export type ManualQuoteV1 = Readonly<MutableManualQuoteV1>;
export type ManualQuoteSetV1 = Readonly<
  Omit<MutableManualQuoteSetV1, "quotes"> & {
    readonly quotes: readonly ManualQuoteV1[];
  }
>;
export type LocalPortfolioRecordV1 = Readonly<
  Omit<MutableLocalPortfolioRecordV1, "document" | "manualQuotes"> & {
    readonly document: PortfolioDocumentV1;
    readonly manualQuotes?: ManualQuoteSetV1;
  }
>;
